import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const AUCTION_STATUS_STYLES = {
  postulations_open:      { label: 'Postulaciones abiertas', color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' },
  postulations:           { label: 'En postulaciones',       color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' },
  closed:                 { label: 'Cerrado',                color: '#6B7A99', bg: '#F0F2F8', border: '#CBD0DB' },
  preparing_to_broadcast: { label: 'Preparando transmisión', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  broadcasting:           { label: 'En transmisión',         color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
};
const DEFAULT_STATUS_STYLE = { label: 'En exhibición', color: '#6B7A99', bg: '#F8F9FC', border: '#E2E5EC' };

const PAGE_SIZE_OPTIONS = [24, 48, 96];

function getStatusStyle(status) {
  return AUCTION_STATUS_STYLES[(status || '').toLowerCase()] || DEFAULT_STATUS_STYLE;
}

/**
 * Individual lot card linking to the auction's showroom on the tenant site.
 */
function LotCard({ lot }) {
  const statusStyle = getStatusStyle(lot.auction_status);
  const href = `https://${lot.tenant_domain}/auctions/${lot.auction_id}/showroom`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderRadius: 10,
        overflow: 'hidden',
        border: '1px solid #E2E5EC',
        textDecoration: 'none',
        boxShadow: '0 1px 3px rgba(2,19,68,0.06)',
        transition: 'box-shadow 0.15s, transform 0.15s',
        cursor: 'pointer',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(2,19,68,0.10), 0 4px 8px rgba(2,19,68,0.04)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(2,19,68,0.06)';
        e.currentTarget.style.transform = 'none';
      }}
    >
      {/* Image placeholder */}
      <div style={{ position: 'relative', width: '100%', paddingTop: '75%', background: '#F0F2F8' }}>
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#CBD0DB',
          fontSize: '2.5rem',
        }}>
          📦
        </div>

        {/* Lot number badge */}
        {lot.lot_number != null && (
          <span style={{
            position: 'absolute',
            top: 8, left: 8,
            background: 'rgba(2,19,68,0.60)',
            color: '#fff',
            fontSize: '0.6875rem',
            fontWeight: 600,
            padding: '3px 8px',
            borderRadius: 6,
          }}>
            Lote {lot.lot_number}
          </span>
        )}

        {/* Status badge */}
        <span style={{
          position: 'absolute',
          top: 8, right: 8,
          fontSize: '0.625rem',
          fontWeight: 600,
          padding: '3px 8px',
          borderRadius: 9999,
          background: statusStyle.bg,
          color: statusStyle.color,
          border: `1px solid ${statusStyle.border}`,
        }}>
          {statusStyle.label}
        </span>
      </div>

      {/* Card body */}
      <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#021344', lineHeight: 1.35 }}>
          {lot.lot_name || `Lote ${lot.lot_number || lot.lot_id}`}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#6B7A99', lineHeight: 1.4, marginTop: 2 }}>
          {lot.auction_name}
        </div>
        <div style={{ marginTop: 'auto', paddingTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{
            fontSize: '0.6875rem',
            fontWeight: 500,
            padding: '2px 8px',
            borderRadius: 9999,
            background: '#EEF2FF',
            color: '#1F50FF',
            border: '1px solid rgba(31,80,255,0.20)',
          }}>
            {lot.tenant_name}
          </span>
          <span style={{ fontSize: '0.6875rem', color: '#9AA3B5' }}>
            Ver remate →
          </span>
        </div>
      </div>
    </a>
  );
}

LotCard.propTypes = {
  lot: PropTypes.shape({
    lot_id: PropTypes.number.isRequired,
    lot_name: PropTypes.string,
    lot_number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    winner_id: PropTypes.number,
    auction_id: PropTypes.number.isRequired,
    auction_name: PropTypes.string.isRequired,
    auction_status: PropTypes.string.isRequired,
    tenant_name: PropTypes.string.isRequired,
    tenant_domain: PropTypes.string.isRequired,
  }).isRequired,
};

const CONTROL_STYLE = {
  padding: '7px 12px',
  borderRadius: 6,
  border: '1px solid #E2E5EC',
  background: '#fff',
  color: '#021344',
  fontSize: '0.8125rem',
  cursor: 'pointer',
  outline: 'none',
  fontFamily: 'inherit',
};

/**
 * Pagination controls.
 */
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…');
    }
  }

  const btnStyle = (active) => ({
    minWidth: 34,
    height: 34,
    padding: '0 10px',
    borderRadius: 6,
    border: active ? 'none' : '1px solid #E2E5EC',
    background: active ? '#1F50FF' : '#fff',
    color: active ? '#fff' : '#4A5568',
    fontSize: '0.8125rem',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: active ? '0 2px 8px rgba(31,80,255,0.25)' : 'none',
    fontFamily: 'inherit',
  });

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, marginTop: 32 }}>
      <button
        onClick={() => onPage(page - 1)}
        disabled={page === 1}
        style={{ ...btnStyle(false), opacity: page === 1 ? 0.4 : 1 }}
      >
        ←
      </button>

      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#9AA3B5', fontSize: '0.8125rem' }}>…</span>
        ) : (
          <button key={p} onClick={() => onPage(p)} style={btnStyle(p === page)}>
            {p}
          </button>
        )
      )}

      <button
        onClick={() => onPage(page + 1)}
        disabled={page === totalPages}
        style={{ ...btnStyle(false), opacity: page === totalPages ? 0.4 : 1 }}
      >
        →
      </button>
    </div>
  );
}

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPage: PropTypes.func.isRequired,
};

/**
 * Exhibition page: shows lots from active auctions in a filterable, paginated grid.
 */
function Showroom({ lots }) {
  const [filterTenant, setFilterTenant] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [filterTenant, filterStatus, search, pageSize]);

  const tenants = useMemo(
    () => [...new Set(lots.map(l => l.tenant_name))].sort(),
    [lots]
  );

  const statuses = useMemo(
    () => [...new Set(lots.map(l => l.auction_status))],
    [lots]
  );

  const filtered = useMemo(() => {
    return lots.filter(l => {
      if (filterTenant && l.tenant_name !== filterTenant) return false;
      if (filterStatus && l.auction_status !== filterStatus) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !(l.lot_name || '').toLowerCase().includes(q) &&
          !(l.auction_name || '').toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [lots, filterTenant, filterStatus, search]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  if (lots.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: '#9AA3B5', fontSize: '0.95rem' }}>
        No hay lotes disponibles en exhibición actualmente.
      </div>
    );
  }

  const start = filtered.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, filtered.length);

  return (
    <div>
      {/* Filter bar */}
      <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center',
        marginBottom: 24,
        padding: '14px 18px',
        background: '#fff',
        borderRadius: 10,
        border: '1px solid #E2E5EC',
        boxShadow: '0 1px 3px rgba(2,19,68,0.06)',
      }}>
        <input
          type="text"
          placeholder="Buscar por lote o remate…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...CONTROL_STYLE, minWidth: 220, flex: 1 }}
        />
        <select value={filterTenant} onChange={e => setFilterTenant(e.target.value)} style={CONTROL_STYLE}>
          <option value="">Todos los clientes</option>
          {tenants.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={CONTROL_STYLE}>
          <option value="">Todos los estados</option>
          {statuses.map(s => (
            <option key={s} value={s}>
              {(AUCTION_STATUS_STYLES[s] || DEFAULT_STATUS_STYLE).label}
            </option>
          ))}
        </select>

        {/* Page size */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <span style={{ fontSize: '0.8125rem', color: '#9AA3B5', whiteSpace: 'nowrap' }}>
            {filtered.length === 0 ? '0 lotes' : `${start}–${end} de ${filtered.length}`}
          </span>
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={CONTROL_STYLE}>
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>{n} por página</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lot grid */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9AA3B5', fontSize: '0.9rem' }}>
          Ningún lote coincide con los filtros seleccionados.
        </div>
      ) : (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 16,
          }}>
            {paginated.map(lot => <LotCard key={lot.lot_id} lot={lot} />)}
          </div>

          <Pagination page={page} totalPages={totalPages} onPage={setPage} />
        </>
      )}
    </div>
  );
}

Showroom.propTypes = {
  lots: PropTypes.array.isRequired,
};

export default Showroom;
